'use client';

import { c as _c } from "react/compiler-runtime";
import { DARK_PATTERN_DATA_PROMPT, isDarkPatternFactiii } from '@shared/all/domains/darkPatterns';
import { FactiiiRequirement, type FactiiiTag, type SpaceTimeTag } from '@shared/all/domains/factiiis';
import { useFactiiiHandlers } from '@shared/utils/hooks/useFactiiiHandlers';
import { type QueryKey } from '@tanstack/react-query';
import { Camera, CheckCircle2, Users } from 'lucide-react';
import Image from 'next/image';
import { useState } from 'react';
import { getUploadSrc } from '../../utils/uploads';
import Button from '../common/Button';
import Input from '../UI/Input';
import { PostReferenceFactiii } from './FactiiiRenderers/PostReferenceFactiii';
import { SourceRequiredFactiii } from './FactiiiRenderers/SourceRequiredFactiii';
import { SpaceTimeFactiii } from './FactiiiRenderers/SpaceTimeFactiii';
import { GroupFactiiisModal } from './GroupFactiiisModal';
type RequirementResult = {
  tag: FactiiiTag;
  results: {
    completed: boolean;
    description: string;
    requirement: FactiiiRequirement;
  }[];
};
type Props = {
  tag: FactiiiTag;
  requirementsMet: RequirementResult[];
  setCameraOpenId: (cameraOpenId: number | null) => void;
  defaultTimezone: {
    value: string;
    label: string;
    offset: number;
  };
  myPostFactiiiCounts: FactiiiTag[];
  setMyPostFactiiiCounts: (myPostFactiiiCounts: FactiiiTag[]) => void;
  addSpaceTime: (tag: FactiiiTag, entry: {
    location?: string;
    date?: string;
    time?: string;
    timezone?: SpaceTimeTag['timezone'];
  }) => boolean;
  removeSpaceTime: (tag: FactiiiTag, index: number) => void;
  queryKey: QueryKey;
  isPrivatePost?: boolean;
};

// The editable body of a selected factiii — requirement inputs, photo, and the
// reset/info/group controls. Extracted from the old RenderFactiii so the
// unified FactiiiSearchResult card can render it as its expanded content.
export const FactiiiTagInputs = t0 => {
  const $ = _c(81);
  const {
    tag,
    requirementsMet,
    setCameraOpenId,
    defaultTimezone,
    myPostFactiiiCounts,
    setMyPostFactiiiCounts,
    addSpaceTime,
    removeSpaceTime,
    queryKey,
    isPrivatePost
  } = t0;
  const {
    isEdited
  } = useFactiiiHandlers();
  const [showGroupModal, setShowGroupModal] = useState(false);
  let t1;
  if ($[0] !== isEdited || $[1] !== queryKey || $[2] !== tag) {
    t1 = () => {
      if (tag.isNewFactiii) {
        return false;
      }
      return isEdited(tag, queryKey);
    };
    $[0] = isEdited;
    $[1] = queryKey;
    $[2] = tag;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isEditedFactiii = t1;
  let t2;
  if ($[4] !== tag.requirements) {
    t2 = [FactiiiRequirement.LOCATION_REQUIRED, FactiiiRequirement.APPROXIMATE_LOCATION, FactiiiRequirement.DEVICE_LOCATION].some(r => tag.requirements.includes(r));
    $[4] = tag.requirements;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const needsLocation = t2;
  let t3;
  if ($[6] !== tag.requirements) {
    t3 = tag.requirements.includes(FactiiiRequirement.TIME_REQUIRED);
    $[6] = tag.requirements;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const needsDate = t3;
  let t4;
  if ($[8] !== tag.postFactiiis[0].photo || $[9] !== tag.postFactiiis[0].uploads || $[10] !== tag.requirements) {
    t4 = tag.requirements.includes(FactiiiRequirement.CAMERA) && !tag.postFactiiis[0].photo && tag.postFactiiis[0].uploads.length === 0;
    $[8] = tag.postFactiiis[0].photo;
    $[9] = tag.postFactiiis[0].uploads;
    $[10] = tag.requirements;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const hasCameraButton = t4;
  let t5;
  if ($[12] !== hasCameraButton || $[13] !== isEditedFactiii || $[14] !== setCameraOpenId || $[15] !== tag.factiiiId || $[16] !== tag.groupId || $[17] !== tag.isRemovedFactiii) {
    t5 = (tag.groupId || hasCameraButton || isEditedFactiii() || tag.isRemovedFactiii) && <div className="mb-2 flex items-center gap-1">{tag.groupId && <Button variant="CLEAR" icon={<Users size={16} />} tooltip="This factiii is part of a group. Click to see other factiiis in the group." onClick={() => setShowGroupModal(true)} classNames="text-primary hover:text-indigo-400" />}{hasCameraButton && <Button variant="CLEAR" icon={<Camera width={20} height={20} />} tooltip="Take a photo" onClick={() => setCameraOpenId(tag.factiiiId)} />}{(isEditedFactiii() || tag.isRemovedFactiii) && <div className="ml-auto text-sm text-textSecondary/70">{tag.isRemovedFactiii ? "Removed" : "Edited"}</div>}</div>;
    $[12] = hasCameraButton;
    $[13] = isEditedFactiii;
    $[14] = setCameraOpenId;
    $[15] = tag.factiiiId;
    $[16] = tag.groupId;
    $[17] = tag.isRemovedFactiii;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  let t6;
  if ($[19] !== setCameraOpenId || $[20] !== tag.factiiiId || $[21] !== tag.name || $[22] !== tag.postFactiiis[0].photo) {
    t6 = tag.postFactiiis[0].photo && <div className="relative mb-2 h-20 w-20"><Image className="h-20 w-20 rounded-lg object-cover" src={tag.postFactiiis[0].photo.uri} alt={tag.name} width={80} height={80} /><Button variant="CLEAR" classNames="absolute bottom-0 right-0 p-1" icon={<Camera width={16} height={16} />} onClick={() => setCameraOpenId(tag.factiiiId)} /></div>;
    $[19] = setCameraOpenId;
    $[20] = tag.factiiiId;
    $[21] = tag.name;
    $[22] = tag.postFactiiis[0].photo;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  let t7;
  if ($[24] !== setCameraOpenId || $[25] !== tag.factiiiId || $[26] !== tag.name || $[27] !== tag.postFactiiis[0].photo || $[28] !== tag.postFactiiis[0].uploads) {
    t7 = !tag.postFactiiis[0].photo && tag.postFactiiis[0].uploads.map(u => <div key={u.id} className="relative mb-2 h-20 w-20"><Image className="h-20 w-20 rounded-lg object-cover" src={getUploadSrc(u)} alt={tag.name} width={80} height={80} /><Button variant="CLEAR" classNames="absolute bottom-0 right-0 p-1" icon={<Camera width={16} height={16} />} onClick={() => setCameraOpenId(tag.factiiiId)} /></div>);
    $[24] = setCameraOpenId;
    $[25] = tag.factiiiId;
    $[26] = tag.name;
    $[27] = tag.postFactiiis[0].photo;
    $[28] = tag.postFactiiis[0].uploads;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  let t8;
  if ($[30] !== addSpaceTime || $[31] !== defaultTimezone || $[32] !== needsDate || $[33] !== needsLocation || $[34] !== removeSpaceTime || $[35] !== tag) {
    t8 = (needsLocation || needsDate) && <SpaceTimeFactiii tag={tag} needsLocation={needsLocation} needsDate={needsDate} allowDevice={tag.requirements.includes(FactiiiRequirement.DEVICE_LOCATION)} defaultTimezone={defaultTimezone} addSpaceTime={addSpaceTime} removeSpaceTime={removeSpaceTime} />;
    $[30] = addSpaceTime;
    $[31] = defaultTimezone;
    $[32] = needsDate;
    $[33] = needsLocation;
    $[34] = removeSpaceTime;
    $[35] = tag;
    $[36] = t8;
  } else {
    t8 = $[36];
  }
  let t9;
  if ($[37] !== myPostFactiiiCounts || $[38] !== setMyPostFactiiiCounts || $[39] !== tag) {
    t9 = tag.requirements.includes(FactiiiRequirement.DATA_REQUIRED) && <Input label={isDarkPatternFactiii(tag) ? DARK_PATTERN_DATA_PROMPT : "Enter Data Here"} value={tag.postFactiiis[0].data ?? ""} icons={tag.postFactiiis[0].data ? [<CheckCircle2 key="ok" className="size-4 text-green-500" />] : undefined} onChange={e => {
      setMyPostFactiiiCounts(myPostFactiiiCounts.map(f => f.factiiiId === tag.factiiiId ? {
        ...f,
        postFactiiis: [{
          ...f.postFactiiis[0],
          data: e
        }]
      } : f));
    }} />;
    $[37] = myPostFactiiiCounts;
    $[38] = setMyPostFactiiiCounts;
    $[39] = tag;
    $[40] = t9;
  } else {
    t9 = $[40];
  }
  let t10;
  if ($[41] !== myPostFactiiiCounts || $[42] !== setMyPostFactiiiCounts || $[43] !== tag.factiiiId || $[44] !== tag.postFactiiis[0].error || $[45] !== tag.postFactiiis[0].number || $[46] !== tag.requirements) {
    t10 = tag.requirements.includes(FactiiiRequirement.INTEGER_REQUIRED) && <div><Input type="number" label="Enter Number Here" value={tag.postFactiiis[0].number?.toString() ?? ""} warning={tag.postFactiiis[0].error} icons={tag.postFactiiis[0].number !== null && !tag.postFactiiis[0].error ? [<CheckCircle2 key="ok" className="size-4 text-green-500" />] : undefined} onChange={e_0 => {
        setMyPostFactiiiCounts(myPostFactiiiCounts.map(f_0 => f_0.factiiiId === tag.factiiiId ? {
          ...f_0,
          postFactiiis: [{
            ...f_0.postFactiiis[0],
            number: e_0 === "" ? null : Number(e_0)
          }]
        } : f_0));
      }} />{tag.postFactiiis[0].error && <p className="mt-1 text-sm text-destructive">Invalid value. Please enter a whole number.</p>}</div>;
    $[41] = myPostFactiiiCounts;
    $[42] = setMyPostFactiiiCounts;
    $[43] = tag.factiiiId;
    $[44] = tag.postFactiiis[0].error;
    $[45] = tag.postFactiiis[0].number;
    $[46] = tag.requirements;
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  let t11;
  if ($[48] !== myPostFactiiiCounts || $[49] !== requirementsMet || $[50] !== setMyPostFactiiiCounts || $[51] !== tag.factiiiId || $[52] !== tag.postFactiiis[0].data || $[53] !== tag.requirements) {
    t11 = tag.requirements.some(_temp) && <Input label="Enter Source Link Here" value={tag.postFactiiis[0].data ?? ""} icons={requirementsMet.find(r_0 => r_0.tag.factiiiId === tag.factiiiId)?.results.find(_temp2)?.completed ? [<CheckCircle2 key="ok" className="size-4 text-green-500" />] : undefined} onChange={e_1 => {
      setMyPostFactiiiCounts(myPostFactiiiCounts.map(f_1 => f_1.factiiiId === tag.factiiiId ? {
        ...f_1,
        postFactiiis: [{
          ...f_1.postFactiiis[0],
          data: e_1
        }]
      } : f_1));
    }} />;
    $[48] = myPostFactiiiCounts;
    $[49] = requirementsMet;
    $[50] = setMyPostFactiiiCounts;
    $[51] = tag.factiiiId;
    $[52] = tag.postFactiiis[0].data;
    $[53] = tag.requirements;
    $[54] = t11;
  } else {
    t11 = $[54];
  }
  let t12;
  if ($[55] !== isPrivatePost || $[56] !== myPostFactiiiCounts || $[57] !== setMyPostFactiiiCounts || $[58] !== tag) {
    t12 = tag.requirements.includes(FactiiiRequirement.POST_REFERENCE_REQUIRED) && <PostReferenceFactiii tag={tag} myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} isPrivatePost={isPrivatePost} />;
    $[55] = isPrivatePost;
    $[56] = myPostFactiiiCounts;
    $[57] = setMyPostFactiiiCounts;
    $[58] = tag;
    $[59] = t12;
  } else {
    t12 = $[59];
  }
  let t13;
  if ($[60] !== myPostFactiiiCounts || $[61] !== setMyPostFactiiiCounts || $[62] !== tag) {
    t13 = tag.requirements.includes(FactiiiRequirement.SOURCE_REQUIRED) && <SourceRequiredFactiii tag={tag} myPostFactiiiCounts={myPostFactiiiCounts} setMyPostFactiiiCounts={setMyPostFactiiiCounts} />;
    $[60] = myPostFactiiiCounts;
    $[61] = setMyPostFactiiiCounts;
    $[62] = tag;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] !== t10 || $[65] !== t11 || $[66] !== t12 || $[67] !== t13 || $[68] !== t8 || $[69] !== t9) {
    t14 = <div className="flex flex-col space-y-4">{t8}{t9}{t10}{t11}{t12}{t13}</div>;
    $[64] = t10;
    $[65] = t11;
    $[66] = t12;
    $[67] = t13;
    $[68] = t8;
    $[69] = t9;
    $[70] = t14;
  } else {
    t14 = $[70];
  }
  let t15;
  if ($[71] !== showGroupModal || $[72] !== tag.groupId || $[73] !== tag.name) {
    t15 = showGroupModal && tag.groupId && <GroupFactiiisModal closeHandler={() => setShowGroupModal(false)} groupId={tag.groupId} currentFactiiiName={tag.name} />;
    $[71] = showGroupModal;
    $[72] = tag.groupId;
    $[73] = tag.name;
    $[74] = t15;
  } else {
    t15 = $[74];
  }
  let t16;
  if ($[75] !== t14 || $[76] !== t15 || $[77] !== t5 || $[78] !== t6 || $[79] !== t7) {
    t16 = <>{t5}{t6}{t7}{t14}{t15}</>;
    $[75] = t14;
    $[76] = t15;
    $[77] = t5;
    $[78] = t6;
    $[79] = t7;
    $[80] = t16;
  } else {
    t16 = $[80];
  }
  return t16;
};
function _temp(requirement) {
  return [FactiiiRequirement.HUMAN_SOURCE, FactiiiRequirement.GOVERNMENT_SOURCE, FactiiiRequirement.ENTERPRISE_SOURCE, FactiiiRequirement.ANONYMOUS_SOURCE].includes(requirement as FactiiiRequirement);
}
function _temp2(r_1) {
  return [FactiiiRequirement.HUMAN_SOURCE, FactiiiRequirement.GOVERNMENT_SOURCE, FactiiiRequirement.ENTERPRISE_SOURCE, FactiiiRequirement.ANONYMOUS_SOURCE].includes(r_1.requirement);
}